<template>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
    <Card class="overflow-hidden border-border/60 shadow-sm">
      <CardHeader class="gap-3 bg-muted/40">
        <div class="flex items-center justify-between gap-3">
          <div>
            <CardTitle class="text-lg">
              Softphone
            </CardTitle>
            <CardDescription>Connect to your PBX and manage calls.</CardDescription>
          </div>
          <Badge v-if="isRegistered" variant="secondary" class="gap-2">
            <span class="h-2 w-2 rounded-full bg-emerald-500" />
            Online
          </Badge>
          <Badge v-else variant="outline" class="gap-2">
            <span class="h-2 w-2 rounded-full bg-amber-500" />
            Offline
          </Badge>
        </div>
      </CardHeader>

      <CardContent class="space-y-6 p-6">
        <div class="flex flex-col lg:flex-row lg:items-center gap-2">
          <Button size="sm" :loading="loadingGettingAgentStatus" @click="fetchAgentStatus(form.extension)">
            Agent Status: <Badge variant="secondary">
              {{ agentStatus ?? 'No Status' }}
            </Badge>
          </Button>
          <Button v-if="agentStatus === 'Logged Out'" :loading="loadingSettingAgentStatus" size="sm" @click="setAgentStatus(form.extension, 'Available')">
            Enter Rotation
          </Button>
          <Button v-if="agentStatus === 'Available'" variant="destructive" :loading="loadingSettingAgentStatus" size="sm" @click="setAgentStatus(form.extension, 'Logged Out')">
            Exit Rotation
          </Button>
        </div>
        <div v-if="!isRegistered" class="space-y-4">
          <div class="space-y-2">
            <Label for="extension">Extension</Label>
            <Input id="extension" v-model="form.extension" placeholder="e.g. 1000" />
          </div>
          <div class="space-y-2">
            <Label for="password">Password</Label>
            <Input id="password" v-model="form.password" type="password" placeholder="••••••••" />
          </div>
          <div class="space-y-2">
            <Button :loading="loadingSetupUserAgent" block @click="handleLogin">
              {{ loadingSetupUserAgent ? 'Connecting...' : 'Connect to PBX' }}
            </Button>
            <p v-if="error" class="text-sm text-destructive">
              {{ error }}
            </p>
            <div class="gap-1 flex justify-between">
              <Button @click="loginAs(1000,'1wErdPTm%.f^8JBacgD8')">
                Connect as 1000
              </Button>
              <Button @click="loginAs(1001,'fCK.?.?MIjjTXjjoA?!p')">
                Connect as 1001
              </Button>
              <Button @click="loginAs(1002,'JhRXzSV0^!2.iF^*rQRN')">
                Connect as 1002
              </Button>
            </div>
          </div>
        </div>

        <div v-else class="space-y-6">
          <div class="flex items-center justify-between gap-3 rounded-lg border bg-card px-3 py-2 text-sm">
            <div class="text-muted-foreground">
              Connected as <span class="font-medium text-foreground">{{ form.extension }}</span>
              <span class="text-muted-foreground">@ pbx.umisis.com</span>
            </div>
            <Button variant="outline" size="sm" :loading="loadingDisconnect" @click="handleLogout">
              Logout
            </Button>
          </div>

          <Separator />


          <div v-if="incomingSession" class="space-y-4 rounded-lg border border-amber-200 bg-amber-50/70 p-4">
            <div>
              <p class="text-sm font-medium text-amber-900">
                Incoming Call
              </p>
              <p class="text-sm text-amber-700">
                From: <span class="font-semibold">{{ incomingSession.remoteIdentity.uri.user }}</span>
              </p>
            </div>
            <div class="flex gap-2">
              <Button class="bg-emerald-600 text-white hover:bg-emerald-600/90" :loading="loadingAnswerCall" @click="answerCall">
                Answer
              </Button>
              <Button variant="destructive" :loading="loadingRejectCall" @click="rejectCall">
                Reject
              </Button>
            </div>
          </div>

          <div v-else class="space-y-4">
            <div v-if="currentSession" class="space-y-3 rounded-lg border bg-muted/30 p-4">
              <p class="text-sm text-muted-foreground">
                On call with
                <span class="font-semibold text-foreground">{{ currentSession.remoteIdentity.uri.user }}</span>
              </p>
              <pre>
                {{ currentSession.remoteIdentity.uri }}
              </pre>
              <div class="flex flex-row justify-between gap-2">
                <Button variant="destructive" block :loading="loadingHangupCall" @click="hangupCall">
                  Hangup
                </Button>
                <Button variant="destructive" block :loading="loadingParkCall" @click="handleParkMyCall">
                  Park Call
                </Button>
              </div>
            </div>

            <div v-else class="space-y-3">
              <div class="space-y-2">
                <Label for="target-number">Number to dial</Label>
                <Input id="target-number" v-model="targetNumber" placeholder="Enter number" />
              </div>
              <div class="flex flex-row justify-between gap-2">
                <Button class="bg-emerald-600 text-white hover:bg-emerald-600/90" :loading="loadingMakeCall" block @click="handleCall">
                  Call
                </Button>
                <Button v-if="form.extension === 1000" class="bg-emerald-600 text-white hover:bg-emerald-600/90" :loading="loadingMakeCall" block @click="dialIntoQueue">
                  Dial Into Queue
                </Button>
              </div>
            </div>
            <Card class="my-4">
              <CardHeader>
                <div>
                  <Button size="sm" variant="outline" class="float-right" :loading="loadingListParkedCalls" @click="handleListParkedCalls">
                    Fetch Parked Calls
                  </Button>
                  Parked Calls
                </div>
              </CardHeader>
              <CardContent>
                <ListboxGroup>
                  <Item v-for="call in parkedCalls" :key="call.uuid">
                    <ItemContent>
                      <ItemTitle class="">
                        Call: {{ call.callerName }} - {{ call.callerNumber }}
                      </ItemTitle>
                      <ItemDescription class="">
                        {{ formatRelative(call.parkedTime) }}
                      </ItemDescription>
                    </ItemContent>
                    <ItemActions>
                      <Button @click="retrieveCall(call.uuid)">
                        Retrieve
                      </Button>
                      <Button :disabled="!currentSession || !currentCall" @click="mergeCalls(call.uuid, currentCall.uuid)">
                        Merge with Current
                      </Button>
                    </ItemActions>
                  </Item>
                </ListboxGroup>
              </CardContent>
            </Card>
          </div>
        </div>
      </CardContent>
    </Card>
    <Card v-if="incomingCallIntent">
      <CardHeader>
        Incoming Call Intent Detected
      </CardHeader>
      <CardContent>
        <div>Caller name: <strong>{{ incomingCallIntent.customer_name }}</strong></div>
        <div>Caller intent: <strong>{{ incomingCallIntent.customer_intent }}</strong></div>
      </CardContent>
    </Card>
    <CustomerOperationsCard />
    <Card>
      <CardHeader>
        DEBUGGING PURPOSES ONLY
      </CardHeader>
      <CardContent class="flex flex-col gap-4">
        <div class="grid grid-cols-2 gap-2">
          <Button variant="outline" block :loading="loadingEnableQueue" @click="handleEnableQueue">
            Simulate incoming queue calls
          </Button>
          <Button variant="destructive" block :loading="loadingDisableQueue" @click="handleDisableQueue">
            Stop simulated  incoming queue calls
          </Button>
        </div>
        <div class="flex flex-row gap-2">
          <Button variant="outline" block :loading="loadingGettingCurrentCall" @click="getCurrentCall">
            Get Active Call Info
          </Button>
          <Button variant="outline" block :loading="loadingFetchChannels" @click="handleFetchChannels">
            Show Active Channels
          </Button>
          <Button variant="outline" block :loading="loadingFetchChannels" @click="channels = []">
            Clear Channels
          </Button>
        </div>
        <div v-if="currentCall">
          <h2>Current Active Call</h2>
          <pre>
            {{ currentCall }}
          </pre>
        </div>

        <div v-if="channels.length > 0">
          <p class="font-medium text-foreground">
            Active Channels
          </p>
          <div v-for="(channel,index) in channels" :key="'channel-' + index" class="flex items-center justify-between gap-2">
            <pre>
                {{ channel }}
              </pre>
          </div>
        </div>
      </CardContent>
    </Card>
    <audio id="remoteAudio" hidden />
  </div>
</template>


<script setup>
import { ref, onUnmounted } from 'vue';
import { useSipUserAgent } from '../composables/useSipUserAgent';
import { useParkingCalls } from '../composables/useParkingCalls';
import { useQueueControls } from '../composables/useQueueControls';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import InputEmail from '../../../../../../resources/js/components/umisis/form/inputs/InputEmail.vue'
import { useMagicKeys, whenever } from '@vueuse/core'
import { useChannels } from '../composables/useChannels.js'
import { ListboxGroup } from 'reka-ui'
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from '../../../../../../resources/js/components/ui/item/index.js'
import { useDate } from '../../../../../../resources/js/composables/formatters/useDate.js'
import { useIncomingCall } from '../composables/useIncomingCall.js'
import CustomerOperationsCard from './CustomerOperationsCard.vue'
const { formatRelative } = useDate()


// The composable handles all the Reverb logic
const { incomingCall, dismissCall } = useIncomingCall();

const { Ctrl_K, Escape, Shift_Enter } = useMagicKeys()

const {
  isConnected,
  isRegistered,
  incomingSession,
  incomingCallIntent,
  currentSession,
  setupUserAgent,
  makeCall,
  answerCall,
  rejectCall,
  hangupCall,
  disconnect,
  getAgentStatus,
  setAgentStatus,
  loadingSetupUserAgent,
  loadingMakeCall,
  loadingAnswerCall,
  loadingRejectCall,
  loadingHangupCall,
  loadingDisconnect,
  loadingGettingAgentStatus,
  loadingSettingAgentStatus,
  agentStatus,
  getCurrentCall,
  currentCall,
  loadingGettingCurrentCall
} = useSipUserAgent();

const {
  parkedCalls,
  listParkedCalls,
  parkMyCall,
  retrieveCall,
  mergeCalls,
  loadingListParkedCalls,
  loadingParkCall,
  loadingRetrieveCall,
  loadingMergeCalls,
} = useParkingCalls();

const {
  enableQueue,
  disableQueue,
  loadingEnableQueue,
  loadingDisableQueue,
} = useQueueControls();

const { loadingFetchChannels, fetchChannels, channels } = useChannels()

const form = ref({ extension: '1001', password: 'fCK.?.?MIjjTXjjoA?!p' });
const targetNumber = ref('');
const error = ref('');
const parkUuid = ref('');
const retrieveUuid = ref('');
const retrieveDest = ref('');
const mergeUuid1 = ref('');
const mergeUuid2 = ref('');
const callerName = ref(null);
const callerPhoneNumber = ref(null);
const callerEmail = ref(null);


const loginAs = (extension, password) => {
  form.value.extension = extension
  form.value.password = password
  handleLogin()
}

const dialIntoQueue = () => {
  makeCall(7000)
}
const handleLogin = async () => {
  error.value = '';
  try {
    await setupUserAgent({
      extension: form.value.extension,
      password: form.value.password,
      domain: 'pbx.umisis.com',
      wssPort: 7443
    });
    agentStatus.value = await getAgentStatus(form.value.extension);
  } catch (err) {
    console.error(err);
    error.value = "Connection failed. Check console/network.";
  }
};

const handleCall = () => {
  if (!targetNumber.value) return;
  makeCall(targetNumber.value);
};

const fetchAgentStatus = async (extension) => {
  agentStatus.value = await getAgentStatus(extension);
};

const handleLogout = () => {
  disconnect();
};

const handleListParkedCalls = async () => {
  await listParkedCalls();
};

const handleParkMyCall = async () => {
  await parkMyCall();
  await listParkedCalls();
};

const handleRetrieveCall = async () => {
  if (!retrieveUuid.value) return;
  await retrieveCall(retrieveUuid.value, retrieveDest.value || undefined);
  await listParkedCalls();
};

const handleMergeCalls = async () => {
  if (!mergeUuid1.value || !mergeUuid2.value) return;
  await mergeCalls(mergeUuid1.value, mergeUuid2.value);
  await listParkedCalls();
};

const handleEnableQueue = async () => {
  await enableQueue();
};

const handleDisableQueue = async () => {
  await disableQueue();
};

const handleFetchChannels = async () => {
  await fetchChannels();
};



// Cleanup when component is destroyed
onUnmounted(() => {
  disconnect();
});
</script>
